import React, { useContext, useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Context
import AuthContext from './context/AuthContext';
import SettingsContext from './context/SettingsContext';

// Pages
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import WorkboardPage from './pages/admin/WorkboardPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OAuthCompletePage from './pages/OAuthCompletePage';
import OAuthSetupPage from './pages/OAuthSetupPage';
import ManageShops from './pages/superadmin/ManageShops';
import ShopUserManagementPage from './pages/superadmin/ShopUserManagementPage';
import WaitlistPage from './pages/superadmin/WaitlistPage';
import ChangePasswordPage from './pages/account/ChangePasswordPage';
import WithdrawPage from './pages/account/WithdrawPage';
import BillingPage from './pages/account/BillingPage';
import BillingCallbackPage from './pages/account/BillingCallbackPage';
import SupportPage from './pages/SupportPage';
import SupportAdminPage from './pages/superadmin/SupportAdminPage';
import AuditLogPage from './pages/superadmin/AuditLogPage';

// Components (ThemeToggleButton replaced with inline sidebar toggle)
import ProtectedRoute from './components/ProtectedRoute';
import PublicOnlyRoute from './components/PublicOnlyRoute';
import TermsAgreePage from './pages/TermsAgreePage';
import SuperAdminRoute from './components/SuperAdminRoute';
import SubscriptionBanner from './components/SubscriptionBanner';
import InstallAppModal from './components/InstallAppModal';
import { isStandalone, isInstalledApp } from './utils/pwaInstall';
import api from './api/axios';

// Icons
import { FaCalendarAlt, FaHome, FaWonSign, FaChartLine, FaListAlt, FaCar, FaUserFriends, FaCog, FaSignOutAlt, FaKey, FaMoon, FaSun, FaShieldAlt, FaCreditCard, FaUserPlus, FaUserTimes, FaMobileAlt, FaHeadset, FaClipboardList } from 'react-icons/fa';
import { LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';

// Permissions
import { hasPermission, getRoleLabel } from './utils/permissions';

// Sidebar component
const AppSidebar = ({ expanded, setExpanded }) => {
    const { user, logout } = useContext(AuthContext);
    const { settings } = useContext(SettingsContext);
    const location = useLocation();
    const ThemeContext = require('./context/ThemeContext').default;
    const { theme, toggleTheme } = useContext(ThemeContext);
    const [profileOpen, setProfileOpen] = useState(false);
    const [installOpen, setInstallOpen] = useState(false);
    const [installedElsewhere, setInstalledElsewhere] = useState(false);
    const [supportUnread, setSupportUnread] = useState(0);
    const profileRef = useRef(null);
    const sidebarRef = useRef(null);
    // 브라우저 탭에서 열었어도 이미 설치돼 있으면(Chromium 감지) 설치 항목을 숨긴다 (파악되는 경우만)
    useEffect(() => {
        let alive = true;
        isInstalledApp().then((v) => { if (alive) setInstalledElsewhere(v); });
        return () => { alive = false; };
    }, []);

    // 이미 앱(standalone)으로 실행 중이거나 이미 설치됨이 감지되면 '앱 설치' 항목 숨김
    const showInstall = !isStandalone() && !installedElsewhere;

    // 고객센터 미확인 건수. 사장님은 '새 답변', 슈퍼는 '확인 필요' 수를 센다.
    // location.pathname 에 의존해 페이지를 옮길 때마다 갱신한다 — 답변을 읽고
    // 목록으로 돌아오면 뱃지가 바로 사라져야 하고, 폴링을 돌릴 만한 값은 아니다.
    useEffect(() => {
        if (!user) return;
        const url = user.role === 'superadmin' ? '/superadmin/support/unread' : '/support/unread';
        let alive = true;
        api.get(url)
            .then(res => { if (alive) setSupportUnread(res.data?.data?.unread || 0); })
            // 직원 계정은 403, 구독 만료 등 어떤 이유로든 실패할 수 있다 — 뱃지는 부가 정보라 조용히 0.
            .catch(() => { if (alive) setSupportUnread(0); });
        return () => { alive = false; };
    }, [user, location.pathname]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setProfileOpen(false);
            }
            // 사이드바 바깥 클릭 시 접기 — 모바일(덮는 서랍)에서만.
            // 데스크톱은 펼치면 본문이 밀리는 구조라, 여기서 접으면 mousedown~mouseup 사이에
            // 본문이 154px 이동해 클릭이 원래 버튼에 안 꽂힌다. 토글 버튼으로만 닫는다.
            const isDrawer = window.matchMedia('(max-width: 768px)').matches;
            if (isDrawer && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
                setExpanded(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setExpanded]);

    if (!user) return null;

    const toggleSidebar = () => {
        setExpanded(prev => !prev);
        if (expanded) setProfileOpen(false);
    };

    const isReservation = !!settings?.use_reservation;
    const mainLinks = [
        // `/` = 홈 (공통), `/workboard` = 워크보드 (예약사업장만)
        { to: '/', icon: <FaHome />, label: '홈', end: true },
        ...(isReservation ? [
            { to: '/workboard', icon: <FaCalendarAlt />, label: '워크보드', end: true },
        ] : []),
        ...(hasPermission(user.role, 'stats:view') ? [
            { to: '/admin/dashboard', icon: <FaWonSign />, label: '대시보드' },
            { to: '/admin/reports', icon: <FaChartLine />, label: '리포트' },
        ] : []),
        ...(hasPermission(user.role, 'records:view') ? [
            { to: '/admin/records', icon: <FaListAlt />, label: '서비스 이력' },
        ] : []),
        ...(hasPermission(user.role, 'vehicles:view') ? [
            { to: '/admin/vehicles', icon: <FaCar />, label: '차량' },
        ] : []),
        ...(hasPermission(user.role, 'records:view') ? [
            { to: '/admin/customers', icon: <FaUserFriends />, label: '고객' },
        ] : []),
        ...(hasPermission(user.role, 'records:edit') ? [
            { to: '/admin/pending-bookings', icon: <FaUserPlus />, label: '예약 요청' },
        ] : []),
    ];

    const isActive = (to, end) => {
        if (end) return location.pathname === to;
        return location.pathname.startsWith(to);
    };

    return (
        <>
        {/* 모바일 전용: 사이드바 접혀있을 때 좌상단 floating 버튼 */}
        {!expanded && (
            <button className="sidebar-mobile-fab" onClick={toggleSidebar} aria-label="메뉴 열기">
                <LuPanelLeftOpen />
            </button>
        )}
        <div ref={sidebarRef} className={`app-sidebar ${expanded ? 'expanded' : ''}`}>
            <div className="sidebar-brand">
                <button className="sidebar-toggle" onClick={toggleSidebar}>
                    {expanded ? <LuPanelLeftClose /> : <LuPanelLeftOpen />}
                </button>
                {expanded && (
                    <>
                        <span className="sidebar-logo">W</span>
                        <span className="sidebar-brand-text">WashNote</span>
                    </>
                )}
            </div>

            <nav className="sidebar-nav">
                {mainLinks.map(link => (
                    <NavLink key={link.to} to={link.to} end={link.end}
                        className={`sidebar-item ${isActive(link.to, link.end) ? 'active' : ''}`}>
                        <span className="sidebar-icon">{link.icon}</span>
                        <span className="sidebar-label">{link.label}</span>
                    </NavLink>
                ))}

                {hasPermission(user.role, 'settings:shop') && (
                    <>
                        <div className="sidebar-divider" />
                        <NavLink to="/settings" className={`sidebar-item ${location.pathname.startsWith('/settings') ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaCog /></span>
                            <span className="sidebar-label">설정</span>
                        </NavLink>
                        <NavLink to="/account/billing" className={`sidebar-item ${location.pathname.startsWith('/account/billing') ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaCreditCard /></span>
                            <span className="sidebar-label">구독 관리</span>
                        </NavLink>
                        <NavLink to="/support" className={`sidebar-item ${location.pathname.startsWith('/support') ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaHeadset /></span>
                            <span className="sidebar-label">고객센터</span>
                            {supportUnread > 0 && <span className="sidebar-badge">{supportUnread}</span>}
                        </NavLink>
                    </>
                )}

                {user.role === 'superadmin' && (
                    <>
                        <NavLink to="/superadmin/shops" className={`sidebar-item ${location.pathname === '/superadmin/shops' ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaShieldAlt /></span>
                            <span className="sidebar-label">매장 관리</span>
                        </NavLink>
                        <NavLink to="/superadmin/waitlist" className={`sidebar-item ${location.pathname === '/superadmin/waitlist' ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaUserPlus /></span>
                            <span className="sidebar-label">사전 신청</span>
                        </NavLink>
                        <NavLink to="/superadmin/support" className={`sidebar-item ${location.pathname.startsWith('/superadmin/support') ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaHeadset /></span>
                            <span className="sidebar-label">고객센터</span>
                            {supportUnread > 0 && <span className="sidebar-badge">{supportUnread}</span>}
                        </NavLink>
                        <NavLink to="/superadmin/audit-logs" className={`sidebar-item ${location.pathname.startsWith('/superadmin/audit-logs') ? 'active' : ''}`}>
                            <span className="sidebar-icon"><FaClipboardList /></span>
                            <span className="sidebar-label">감사 로그</span>
                        </NavLink>
                    </>
                )}
            </nav>

            <div className="sidebar-bottom" ref={profileRef}>
                {showInstall && (
                    <button className="sidebar-item" onClick={() => setInstallOpen(true)}>
                        <span className="sidebar-icon"><FaMobileAlt /></span>
                        <span className="sidebar-label">앱 설치</span>
                    </button>
                )}
                <button className="sidebar-item" onClick={toggleTheme}>
                    <span className="sidebar-icon">{theme === 'light' ? <FaMoon /> : <FaSun />}</span>
                    <span className="sidebar-label">{theme === 'light' ? '다크 모드' : '라이트 모드'}</span>
                </button>
                <div className="sidebar-divider" />
                <div className="sidebar-profile-wrapper">
                    {profileOpen && (
                        <div className="sidebar-profile-popover">
                            <div className="profile-popover-header">
                                <div className="profile-popover-avatar">{(user.displayName || user.username)?.charAt(0)?.toUpperCase()}</div>
                                <div className="profile-popover-info">
                                    <div className="profile-popover-name">{user.displayName || user.username}</div>
                                    <div className="profile-popover-role">{getRoleLabel(user.role)}</div>
                                </div>
                            </div>
                            <div className="profile-popover-divider" />
                            <Link to="/account/change-password" className="profile-popover-item" onClick={() => setProfileOpen(false)}>
                                <FaKey /> 비밀번호 변경
                            </Link>
                            {user.role === 'admin' && (
                                <Link to="/account/withdraw" className="profile-popover-item" onClick={() => setProfileOpen(false)}>
                                    <FaUserTimes /> 회원 탈퇴
                                </Link>
                            )}
                            <button className="profile-popover-item profile-popover-logout" onClick={() => { setProfileOpen(false); logout(); }}>
                                <FaSignOutAlt /> 로그아웃
                            </button>
                        </div>
                    )}
                    <button className="sidebar-user" onClick={() => setProfileOpen(prev => !prev)}>
                        <div className="sidebar-avatar sidebar-collapsed-only">{(user.displayName || user.username)?.charAt(0)?.toUpperCase()}</div>
                        <div className="sidebar-shop-badge sidebar-expanded-only">{settings?.shopName || 'Shop'}</div>
                        <span className="sidebar-username">{user.displayName || user.username}</span>
                    </button>
                </div>
            </div>
        </div>
        <InstallAppModal show={installOpen} onClose={() => setInstallOpen(false)} />
        </>
    );
};

function App() {
    const { user } = useContext(AuthContext);
    const { settings, loading: settingsLoading } = useContext(SettingsContext);
    // 펼침 상태는 여기서 들고 있는다 — 사이드바와 본문(app-content) 이 형제라
    // 본문이 밀려나려면 공통 부모가 알아야 한다.
    const [sidebarExpanded, setSidebarExpanded] = useState(false);

    return (
        <Router>
            <div className="app-layout">
                <AppSidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />
                <main className={`app-content ${sidebarExpanded ? 'sidebar-expanded' : ''} ${!user ? 'no-sidebar' : ''}`}>
                    {user && <SubscriptionBanner />}
                    <Routes>
                        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
                        <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
                        {/* /oauth/complete 는 네이버 콜백 중간 상태 처리 — PublicOnlyRoute 를 걸지 않는다 */}
                        <Route path="/oauth/complete" element={<OAuthCompletePage />} />
                        <Route path="/oauth/setup" element={<PublicOnlyRoute><OAuthSetupPage /></PublicOnlyRoute>} />
                        <Route path="/terms-agree" element={<ProtectedRoute><TermsAgreePage /></ProtectedRoute>} />
                        <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                        <Route path="/workboard" element={
                            <ProtectedRoute>
                                {settingsLoading ? null : (!!settings?.use_reservation ? <WorkboardPage /> : <Navigate to="/" replace />)}
                            </ProtectedRoute>
                        } />
                        <Route path="/admin/*" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                        <Route path="/settings/*" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                        <Route path="/account/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
                        <Route path="/account/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
                        <Route path="/account/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                        <Route path="/account/billing/callback" element={<ProtectedRoute><BillingCallbackPage /></ProtectedRoute>} />
                        <Route path="/superadmin/shops" element={<SuperAdminRoute><ManageShops /></SuperAdminRoute>} />
                        <Route path="/superadmin/shops/:shopId/users" element={<SuperAdminRoute><ShopUserManagementPage /></SuperAdminRoute>} />
                        <Route path="/superadmin/waitlist" element={<SuperAdminRoute><WaitlistPage /></SuperAdminRoute>} />
                        <Route path="/superadmin/support" element={<SuperAdminRoute><SupportAdminPage /></SuperAdminRoute>} />
                        <Route path="/superadmin/audit-logs" element={<SuperAdminRoute><AuditLogPage /></SuperAdminRoute>} />
                        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
                    </Routes>
                </main>
            </div>
        </Router>
    );
}

export default App;
